import { Box, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useForm, FormProvider } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";

import { Heading } from "../../components/Heading";
import GroupFormFields from "../../components/GroupFormData";
import { useCreateGroupMutation } from "../../api/mutations/useCreateGroupMutation";

import { groupFormSchema } from "../../schema/group.schema";
import { GroupFormData } from "../../types/group.types";
import { toast } from "react-toastify";
import { getEntityProfileRoute } from "../../../../routers/routeUtils";

const CreateEntityGroup = () => {
  const { t } = useTranslation("groups");
  const { entityId } = useParams<{ entityId: string }>();
  const navigate = useNavigate();
  const createGroupMutation = useCreateGroupMutation();

  const methods = useForm<GroupFormData>({
    resolver: zodResolver(groupFormSchema),
    defaultValues: {
      name: "",
      description: "",
      imageLink: "",
      users: [],
      groupPrivacy: "public",
    },
  });

  const onSubmit = async (data: GroupFormData) => {
    try {
      await createGroupMutation.mutateAsync({
        entityId: Number(entityId),
        data,
      });
      toast.success(t("groupCreatedSuccessfully"));
      setTimeout(() => {
        navigate(`${getEntityProfileRoute(Number(entityId))}?tab=groups`);
      }, 1000);
    } catch (error) {
      toast.error(t("failedToCreateGroup"));
      console.error("Failed to create group:", error);
    }
  };

  return (
    <Box sx={styles.container}>
      <Heading title={t("createGroup")} />
      <FormProvider {...methods}>
        <Box component="form" onSubmit={methods.handleSubmit(onSubmit)} sx={styles.formContainer}>
          <GroupFormFields />

          <Box sx={styles.submitButtonContainer}>
            <Button
              variant="primary-sm"
              type="submit"
              disabled={createGroupMutation.isPending}
              sx={styles.submitButton}
            >
              {t("createGroup")}
            </Button>
          </Box>
        </Box>
      </FormProvider>
    </Box>
  );
};

export default CreateEntityGroup;

const styles = {
  container: {
    borderRadius: "8px",
    border: "1px solid #EAECF0",
    background: "#FFF",
    padding: "24px 16px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "32px",
  },
  formContainer: { width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: "24px" },
  submitButton: {
    width: { xs: "100%", md: "fit-content" },
  },
  submitButtonContainer: {
    padding: "0 16px 12px",
    width: "100%",
    display: "flex",
    justifyContent: "flex-end",
  },
};
